import { writable, derived } from 'svelte/store';
import { supabase } from '../lib/supabase';
import { user } from './auth';

export interface UserLanguage {
  id: string;
  user_id: string;
  target_language_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  language?: {
    id: string;
    name: string;
    code: string;
  };
}

// Stores
export const userLanguages = writable<UserLanguage[]>([]);
export const isLoadingLanguages = writable(false);
export const languagesError = writable<string | null>(null);

// Derived store for active language
export const activeLanguage = derived(userLanguages, ($userLanguages) =>
  $userLanguages.find((lang) => lang.is_active) || null
);

// User languages actions
export const userLanguagesActions = {
  // Load user's languages
  async loadUserLanguages() {
    isLoadingLanguages.set(true);
    languagesError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        userLanguages.set([]);
        isLoadingLanguages.set(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_languages')
        .select(
          `
          *,
          language:flashcard_target_languages(id, name, code)
        `
        )
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      userLanguages.set(data || []);
    } catch (error) {
      console.error('Error loading user languages:', error);
      languagesError.set(error instanceof Error ? error.message : 'Failed to load languages');
      userLanguages.set([]);
    } finally {
      isLoadingLanguages.set(false);
    }
  },

  // Add a new language for the user
  async addLanguage(targetLanguageId: string, setAsActive = false) {
    languagesError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        throw new Error('No user logged in');
      }

      // Check if language already exists
      const { data: existing } = await supabase
        .from('user_languages')
        .select('id')
        .eq('user_id', currentUser.id)
        .eq('target_language_id', targetLanguageId)
        .single();

      if (existing) {
        throw new Error('Language already added');
      }

      const { error } = await supabase.from('user_languages').insert({
        user_id: currentUser.id,
        target_language_id: targetLanguageId,
        is_active: setAsActive,
      });

      if (error) throw error;

      // Reload languages
      await userLanguagesActions.loadUserLanguages();
    } catch (error) {
      console.error('Error adding language:', error);
      languagesError.set(error instanceof Error ? error.message : 'Failed to add language');
      throw error;
    }
  },

  // Set a language as active
  async setActiveLanguage(userLanguageId: string) {
    languagesError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        throw new Error('No user logged in');
      }

      // Update the language to be active (trigger will handle deactivating others)
      const { error } = await supabase
        .from('user_languages')
        .update({ is_active: true })
        .eq('id', userLanguageId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      // Reload languages
      await userLanguagesActions.loadUserLanguages();
    } catch (error) {
      console.error('Error setting active language:', error);
      languagesError.set(error instanceof Error ? error.message : 'Failed to set active language');
      throw error;
    }
  },

  // Remove a language
  async removeLanguage(userLanguageId: string) {
    languagesError.set(null);

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();

      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const { error } = await supabase
        .from('user_languages')
        .delete()
        .eq('id', userLanguageId)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      // Reload languages
      await userLanguagesActions.loadUserLanguages();
    } catch (error) {
      console.error('Error removing language:', error);
      languagesError.set(error instanceof Error ? error.message : 'Failed to remove language');
      throw error;
    }
  },
};

// Initialize languages when user changes
user.subscribe(($user) => {
  if ($user) {
    userLanguagesActions.loadUserLanguages();
  } else {
    userLanguages.set([]);
  }
});
